import type { ReactNode } from "react";
import { DateCalendar } from "@mui/x-date-pickers";
import type { DateTime } from "luxon";
import "./DayCalendar.scss";
import IconChevronDown from "@/shared/components/generic/icons/IconChevronDown.tsx";
import IconChevronLeft from "@/shared/components/generic/icons/IconChevronLeft.tsx";
import IconChevronRight from "@/shared/components/generic/icons/IconChevronRight.tsx";
import type { PickerSelectionState } from "@mui/x-date-pickers/internals";
import { DateStyle } from "@/shared/utils/Dates.ts";
import useDateFormatter from "@/shared/custom-hook/useDateFormatter.ts";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates";

export interface DayCalendarProps {
  date: DateTime | undefined;
  timeZone?: string;
  onDateChanged: (date: DateTime) => void;
  onDateSet: () => void;
  minDate?: DateTime;
  maxDate?: DateTime;
  shouldDisableDate?: (d: DateTime) => boolean;
}

export default function DayCalendar(props: DayCalendarProps): ReactNode {
  const { formatDate } = useDateFormatter();

  return (
    <div className="day-calendar">
      <DateCalendar
        value={props.date}
        timezone={props.timeZone ?? LEGACY_TIME_ZONE}
        onChange={(
          date: DateTime,
          selectionState: PickerSelectionState | undefined,
        ): void => {
          props.onDateChanged(date);
          if (selectionState === "finish") props.onDateSet();
        }}
        minDate={props.minDate}
        maxDate={props.maxDate}
        views={["year", "month", "day"]}
        slots={{
          switchViewIcon: IconChevronDown,
          leftArrowIcon: IconChevronLeft,
          rightArrowIcon: IconChevronRight,
        }}
        showDaysOutsideCurrentMonth={true}
        dayOfWeekFormatter={(date) =>
          formatDate(date.toJSDate(), DateStyle.DATE_WEEKDAY).substring(0, 2)
        }
        shouldDisableDate={props.shouldDisableDate}
        slotProps={{
          calendarHeader: {
            sx: {
              position: "relative",
              "& .MuiPickersArrowSwitcher-root": {
                width: 0,
              },
              "& .MuiPickersCalendarHeader-labelContainer": {
                margin: "auto",
              },
              "& .MuiIconButton-edgeEnd": {
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                height: "40px",
                width: "40px",
                marginLeft: "28px",
              },
              "& .MuiIconButton-edgeStart": {
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                height: "40px",
                width: "40px",
                marginRight: "28px",
              },
            },
          },
        }}
      />
    </div>
  );
}
